import { createContext, useContext, useMemo, type ReactNode } from 'react'

/**
 * ClickSourceContext
 *
 * Allows surface-specific wrappers (e.g. the browser-extension sidepanel, v2
 * chat, the first-query products carousel) to inject a prefix onto the
 * hardcoded `source` values used by shared chat/result cards. This makes
 * buy-link clicks (written to `link_clicks.metadata.src` via
 * `convertToRedirectUrl`) and analytics events (`Dupe Result Clicked`,
 * `Performed Dupe`) attributable to the surface that rendered them.
 *
 * Providers compose: nested providers chain their prefixes onto the parent's,
 * so e.g. an extension-rendered v2 first-row card emits
 * `ext-v2-firstrow-product-list-card`. Order is outer-to-inner.
 *
 * Web chat pages render without a provider, so card sources stay unprefixed.
 */
interface ClickSourceContextValue {
  prefix: string
}

const ClickSourceContext = createContext<ClickSourceContextValue>({
  prefix: '',
})

export const ClickSourceProvider = ({
  prefix,
  children,
}: {
  prefix: string
  children: ReactNode
}) => {
  const { prefix: parentPrefix } = useContext(ClickSourceContext)
  const value = useMemo(
    () => ({ prefix: `${parentPrefix}${prefix}` }),
    [parentPrefix, prefix]
  )
  return (
    <ClickSourceContext.Provider value={value}>
      {children}
    </ClickSourceContext.Provider>
  )
}

/**
 * Returns `baseSource` prefixed with any active ClickSourceContext prefix, or
 * the unchanged `baseSource` when rendered outside a provider.
 */
export const useClickSource = (baseSource: string): string => {
  const { prefix } = useContext(ClickSourceContext)
  return prefix ? `${prefix}${baseSource}` : baseSource
}
